import { useEffect, useState } from 'react';
import L from 'leaflet';
import { loggerState, LOG_COLORS } from '../hooks/useLoggers';
import { LOGGER_SVG } from './LoggerIcon';

const icon = (st, sel) => L.divIcon({
  className: 'logger-icon',
  html: `<div class="lg-pin lg-${st} ${sel ? 'lg-sel' : ''}" style="--lg:${LOG_COLORS[st]}" data-testid="logger-pin-${st}">${LOGGER_SVG(17, '#fff')}</div>`,
  iconSize: [30, 30], iconAnchor: [15, 15],
});

// active noise loggers; colour refreshes every minute
export default function useLoggerLayer(mapRef, loggers, selId, onClick) {
  const [tick, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((x) => x + 1), 60000); return () => clearInterval(id); }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const now = Date.now();
    const ms = loggers.map((l) => L.marker([l.lat, l.lon], { icon: icon(loggerState(l, now), l.id === selId), zIndexOffset: 800, title: l.name })
      .on('click', (e) => { L.DomEvent.stopPropagation(e); onClick(l.id); }).addTo(map));
    return () => ms.forEach((m) => m.remove());
  }, [mapRef, loggers, selId, onClick, tick]);
}

const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
export const NOISE_HTML = '<div class="ns-pin" data-testid="noise-pin"><i></i></div>';

// noise finding spots: red pulsing points where to correlate
export function useNoiseLayer(mapRef, spots, t) {
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const ms = spots.map((s) => L.marker([s.lat, s.lon], { icon: L.divIcon({ className: 'noise-icon', html: NOISE_HTML, iconSize: [34, 34], iconAnchor: [17, 17] }), zIndexOffset: 600, title: s.name })
      .bindPopup(`<b>🔊 ${esc(s.name)}</b><br>${esc(t.lcNoise)} · ${new Date(s.at).toLocaleString(t.locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}${s.note ? `<br>${esc(s.note)}` : ''}`).addTo(map));
    return () => ms.forEach((m) => m.remove());
  }, [mapRef, spots, t]);
}

// planned (not yet deployed) logger spots: grey dashed pins with number
export function usePlanLayer(mapRef, plan = []) {
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const ms = plan.filter((p) => !p.doneAt).map((p) => L.marker([p.lat, p.lon], {
      icon: L.divIcon({ className: 'logger-icon', html: `<div class="lg-plan" data-testid="logger-plan-pin">${plan.indexOf(p) + 1}</div>`, iconSize: [26, 26], iconAnchor: [13, 13] }),
      zIndexOffset: 700, title: p.name, interactive: false, keyboard: false,
    }).addTo(map));
    const open = plan.filter((p) => !p.doneAt);
    if (open.length > 1) ms.push(L.polyline(open.map((p) => [p.lat, p.lon]), { color: '#1d6fe8', weight: 3, dashArray: '6 8', opacity: 0.8, interactive: false }).addTo(map));
    return () => ms.forEach((m) => m.remove());
  }, [mapRef, plan]);
}

// preview of suggested logger positions (not yet in the plan)
export function useSuggestLayer(mapRef) {
  const [list, setList] = useState([]);
  useEffect(() => { const on = (e) => setList(e.detail || []); window.addEventListener('giss-lp-suggest', on); return () => window.removeEventListener('giss-lp-suggest', on); }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !list.length) return undefined;
    const ms = list.map((s) => L.marker([s.lat, s.lon], { icon: L.divIcon({ className: 'logger-icon', html: `<div class="lg-sugg k-${s.kind}" data-testid="logger-suggest-pin"></div>`, iconSize: [18, 18], iconAnchor: [9, 9] }), zIndexOffset: 650, title: s.name, interactive: false, keyboard: false }).addTo(map));
    return () => ms.forEach((m) => m.remove());
  }, [mapRef, list]);
}
